import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * CustomDropdown — Design system dropdown (Phase 7)
 * Replaces all native <select> elements system-wide.
 *
 * IMPORTANT: Renders dropdown inline (NOT via createPortal) so that
 * Radix Dialog's DismissableLayer correctly treats clicks on options
 * as "inside" the React tree. Uses position:fixed + z-index:99999
 * to visually appear above all overlays.
 *
 * NOTE: When inside a CSS-transformed parent (like Radix Dialog),
 * position:fixed behaves like position:absolute relative to that
 * parent. We detect this and adjust coordinates accordingly.
 *
 * Props:
 *   options: Array<{ value: string, label: string }>
 *   value: string
 *   onChange: (value: string) => void
 *   placeholder?: string
 *   label?: string
 *   badge?: string
 *   disabled?: boolean
 */
const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = 'Selecionar...',
  label,
  badge,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  /**
   * Find the nearest ancestor with a CSS transform (creates a new containing block
   * for position:fixed). Returns its getBoundingClientRect or null if none found.
   */
  const getTransformedAncestorRect = useCallback(() => {
    let el = containerRef.current?.parentElement;
    while (el && el !== document.documentElement) {
      const style = window.getComputedStyle(el);
      const transform = style.transform || style.webkitTransform;
      if (transform && transform !== 'none') {
        return el.getBoundingClientRect();
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  /**
   * Calculate dropdown position.
   * If inside a transformed ancestor, subtract its offset since position:fixed
   * becomes relative to that ancestor.
   */
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return { top: 0, left: 0, width: 0, openUp: false };
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const ancestorRect = getTransformedAncestorRect();

    // Offset to subtract if inside a transformed parent
    const offsetX = ancestorRect ? ancestorRect.left : 0;
    const offsetY = ancestorRect ? ancestorRect.top : 0;

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const dropdownHeight = Math.min(options.length * 42 + 12, 280);
    const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    return {
      top: shouldOpenUp
        ? triggerRect.top - dropdownHeight - 4 - offsetY
        : triggerRect.bottom + 4 - offsetY,
      left: triggerRect.left - offsetX,
      width: triggerRect.width,
      openUp: shouldOpenUp,
    };
  }, [options.length, getTransformedAncestorRect]);

  // Open handler: calc position synchronously THEN open
  const handleOpen = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    const pos = calcPosition();
    setPosition(pos);
    setIsOpen(true);
  }, [disabled, isOpen, calcPosition]);

  // Close on outside click / scroll / resize
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = (e) => {
      // Don't close if scrolling inside the dropdown itself
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      // Don't close if scrolling inside our container
      if (containerRef.current && containerRef.current.contains(e.target)) return;
      setIsOpen(false);
    };

    const handleResize = () => setIsOpen(false);

    // Delay adding click listener to avoid closing on the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 10);

    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleSelect = (optValue) => {
    onChange(optValue);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') setIsOpen(false);
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {/* Label + Badge row */}
      {(label || badge) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px',
        }}>
          {label && (
            <span style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--dp-text-secondary)',
              fontFamily: 'var(--dp-font)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              {label}
            </span>
          )}
          {badge && (
            <span style={{
              fontSize: '10px',
              fontWeight: 600,
              color: 'var(--dp-green-500, #34D399)',
              background: 'rgba(52, 211, 153, 0.1)',
              padding: '2px 8px',
              borderRadius: '9999px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontFamily: 'var(--dp-font)',
            }}>
              {badge}
            </span>
          )}
        </div>
      )}

      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'var(--dp-bg-card)',
          border: `1px solid ${isOpen ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-subtle)'}`,
          borderRadius: '8px',
          fontSize: '13px',
          color: selectedOption ? 'var(--dp-text-primary)' : 'var(--dp-text-tertiary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
          fontFamily: 'var(--dp-font)',
          boxShadow: isOpen
            ? '0 0 0 3px rgba(13, 174, 255, 0.15), 0 4px 16px rgba(0, 0, 0, 0.2)'
            : 'none',
          outline: 'none',
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.3)';
            e.currentTarget.style.boxShadow = '0 0 0 2px rgba(13, 174, 255, 0.08)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          style={{
            color: 'var(--dp-text-tertiary)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
            marginLeft: '8px',
          }}
        />
      </div>

      {/* Dropdown rendered INLINE (not portal) so Radix Dialog treats clicks as "inside" */}
      {isOpen && (
        <div
          ref={dropdownRef}
          role="listbox"
          data-custom-dropdown="true"
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
            maxHeight: '280px',
            overflowY: 'auto',
            background: 'var(--dp-bg-elevated, rgba(30, 30, 36, 0.95))',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(13, 174, 255, 0.15)',
            borderRadius: '10px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(13, 174, 255, 0.05)',
            zIndex: 99999,
            padding: '4px',
            animation: position.openUp
              ? 'dpDropdownSlideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'dpDropdownSlideDown 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {options.map((option, index) => {
            const isSelected = String(option.value) === String(value);
            return (
              <div
                key={option.value != null ? String(option.value) : `opt-${index}`}
                role="option"
                aria-selected={isSelected}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(option.value);
                }}
                onMouseDown={(e) => {
                  // Prevent focus loss and Radix from seeing this as outside click
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontFamily: 'var(--dp-font)',
                  color: isSelected ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-text-primary)',
                  fontWeight: isSelected ? 500 : 400,
                  background: isSelected ? 'rgba(13, 174, 255, 0.08)' : 'transparent',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  borderLeft: isSelected ? '3px solid var(--dp-blue-500, #0DAEFF)' : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)';
                    e.currentTarget.style.borderLeftColor = 'rgba(13, 174, 255, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderLeftColor = 'transparent';
                  }
                }}
              >
                {/* Blue dot for selected */}
                {isSelected && (
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'var(--dp-blue-500, #0DAEFF)',
                    flexShrink: 0,
                  }} />
                )}
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Inject keyframe animation */}
      <style>{`
        @keyframes dpDropdownSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dpDropdownSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CustomDropdown;
